import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import AuthService from "../../api/AuthService";
import styles from "./style";

const RELS = ["Spouse", "Child", "Parent", "Other"];
const RELATION_MAX = { Spouse: 1, Child: 5, Parent: 2, Other: 2 };

const BeneficiaryModal = ({ open, onClose, employeeId, creditTypeId, handleBeniSaved, userData, creditId, handleUpdateBeneficiary, beneficiariesList, handleDeleteBeneficiary, getAllBenificiaries, getAllCredits, selectedItem }) => {
  const [creditBeneficiariesData, setCreditBeneficiariesData] = useState(null);
  const [availableRelationships, setAvailableRelationships] = useState(RELS);

  const normalizeRateType = (type) => {
    if (!type) return "Fixed";
    const lowerType = String(type).toLowerCase();
    if (lowerType === "amount") return "Fixed";
    if (lowerType === "percentage") return "Percentage";
    return type;
  };

  // Helper function to safely parse date and convert to ISO format
  const parseDateToISO = (dateString) => {
    if (!dateString) return "";
    
    try {
      // Try to parse various date formats
      let date;
      
      // Check if it's already in ISO format (YYYY-MM-DD)
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString);
      }
      // Check for YYYY/MM/DD format
      else if (dateString.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
        date = new Date(dateString.replace(/\//g, "-"));
      }
      // Check for DD/MM/YYYY or MM/DD/YYYY format (both match same pattern)
      else if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const parts = dateString.split("/");
        const firstPart = parseInt(parts[0], 10);
        const secondPart = parseInt(parts[1], 10);
        
        // If first part > 12, it must be DD/MM/YYYY
        if (firstPart > 12) {
          date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
        // If second part > 12, it must be MM/DD/YYYY
        else if (secondPart > 12) {
          date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
        }
        // Ambiguous case - try DD/MM/YYYY first, then MM/DD/YYYY
        else {
          date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          if (isNaN(date.getTime())) {
            date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
          }
        }
      }
      // Try default Date parsing
      else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "";
      }
      
      return date.toISOString().split('T')[0];
    } catch (error) {
      return "";
    }
  };

  useEffect(() => {
    const fetchCreditBeneficiaries = async () => {
      if (open && creditId) {
        try {
          const result = await AuthService.getCreditBeneficiaries(creditId);
          if (result?.success && result?.data) {
            setCreditBeneficiariesData(result.data);
            if (result.data.beneficiaries && Array.isArray(result.data.beneficiaries)) {
              const types = result.data.beneficiaries.map(b => b.beneficiary_type).filter(Boolean);
              const uniqueTypes = [...new Set(types)];
              setAvailableRelationships(uniqueTypes.length > 0 ? uniqueTypes : RELS);
            }
          }
        } catch (error) {
          console.error("Error fetching credit beneficiaries:", error);
          setAvailableRelationships(RELS);
        }
      }
    };
    fetchCreditBeneficiaries();
  }, [open, creditId]);

  const getRateDetails = (relationship) => {
    // First, try to get from API data
    if (creditBeneficiariesData?.beneficiaries && Array.isArray(creditBeneficiariesData.beneficiaries)) {
      const beneficiary = creditBeneficiariesData.beneficiaries.find(b => b.beneficiary_type === relationship);
      if (beneficiary) {
        return {
          rate: beneficiary.value ?? "",
          rateType: normalizeRateType(beneficiary.rateType),
        };
      }
    }

    // Fallback to selectedItem if API data not available
    if (!selectedItem) {
      return { rate: "", rateType: "Fixed" };
    }

    switch (relationship) {
      case "Spouse":
        return {
          rate: selectedItem?.spouse_amt ?? "",
          rateType: normalizeRateType(selectedItem?.spouse_amt_type),
        };
      case "Child":
        return {
          rate: selectedItem?.child_amt ?? "",
          rateType: normalizeRateType(selectedItem?.child_amt_type),
        };
      case "Parent":
        return {
          rate: selectedItem?.parent_amt ?? "",
          rateType: normalizeRateType(selectedItem?.parent_amt_type),
        };
      case "Other":
        return {
          rate: selectedItem?.additional_beneficiary_amt ?? "",
          rateType: normalizeRateType(selectedItem?.additional_beneficiary_amt_type),
        };
      default:
        return { rate: "", rateType: "Fixed" };
    }
  };

  const [beneficiaries, setBeneficiaries] = useState(() => {
    const initialDetails = getRateDetails(RELS[0]);
    return [
      {
        id: undefined,
        name: "",
        relationship: RELS[0],
        dob: "",
        rate: initialDetails.rate,
        rateType: initialDetails.rateType,
      },
    ];
  });

  useEffect(() => {
    if (beneficiariesList && Array.isArray(beneficiariesList)) {
      if (beneficiariesList.length > 0) {
        const formattedBeneficiaries = beneficiariesList.map(b => ({
          id: b._id,
          name: b.beneficiaryName,
          relationship: b.realtionship,
          dob: parseDateToISO(b.dob),
          rate: b.rate,
          rateType: normalizeRateType(b.rateType)
        }));
        setBeneficiaries(formattedBeneficiaries);
        setOriginalBeneficiaries(beneficiariesList);
      } else {
        // Use first available relationship from API or fallback to RELS[0]
        const initialRel = availableRelationships.length > 0 ? availableRelationships[0] : RELS[0];
        const { rate, rateType } = getRateDetails(initialRel);
        setBeneficiaries([
          {
            id: undefined,
            name: "",
            relationship: initialRel,
            dob: "",
            rate,
            rateType,
          }
        ]);
        setOriginalBeneficiaries([]);
      }
    }
  }, [beneficiariesList, selectedItem, availableRelationships]);

  useEffect(() => {
    setBeneficiaries(prev =>
      prev.map(b => {
        if (b.id) return b;
        const { rate, rateType } = getRateDetails(b.relationship);
        return {
          ...b,
          rate,
          rateType,
        };
      })
    );
  }, [selectedItem, creditBeneficiariesData]);

  const totalRate = useMemo(() => {
    return beneficiaries.reduce((sum, b) => {
      const rate = Number(b.rate) || 0;
      return sum + rate;
    }, 0);
  }, [beneficiaries]);

  const [originalBeneficiaries, setOriginalBeneficiaries] = useState([]);

  const relationshipCounts = useMemo(() => {
    return beneficiaries.reduce((acc, item) => {
      const rel = item.relationship;
      if (!acc[rel]) acc[rel] = 0;
      acc[rel] += 1;
      return acc;
    }, {});
  }, [beneficiaries]);

  const findAvailableRelationship = () => {
    const relationshipsToUse = availableRelationships.length > 0 ? availableRelationships : RELS;
    return relationshipsToUse.find(rel => {
      // Don't allow "Employee" to be added as a new relationship
      if (rel === "Employee") return false;
      const currentCount = relationshipCounts[rel] || 0;
      const maxCount = RELATION_MAX[rel] || 999;
      return currentCount < maxCount;
    }) || null;
  };

  const canAddMoreBeneficiaries = useMemo(
    () => {
      const relationshipsToUse = availableRelationships.length > 0 ? availableRelationships : RELS;
      return relationshipsToUse.some(rel => {
        // Don't allow "Employee" to be added as a new relationship
        if (rel === "Employee") return false;
        const currentCount = relationshipCounts[rel] || 0;
        const maxCount = RELATION_MAX[rel] || 999;
        return currentCount < maxCount;
      });
    },
    [relationshipCounts, availableRelationships]
  );

  const hasChanges = useMemo(() => {
    if (!beneficiaries || beneficiaries.length === 0) return false;

    // Check for new non-Employee rows with all required fields filled
    const hasNew = beneficiaries.some(b => {
      if (b.id) return false; // Skip existing rows
      if (b.relationship === "Employee") return true; // Employee rows are always considered valid
      
      // For new non-Employee rows, check if all required fields are filled
      const hasName = b.name && b.name.trim() !== "";
      const hasDob = b.dob && b.dob !== "";
      const hasRate = String(b.rate ?? '').trim() !== "";
      
      return hasName && hasDob && hasRate;
    });

    // Check for changes in existing rows (excluding Employee rows from change detection)
    const hasExistingChanges = beneficiaries.some(b => {
      if (!b.id) return false;
      if (b.relationship === "Employee") return false; // Employee rows can't be changed
      
      const original = originalBeneficiaries.find(ob => ob._id === b.id);
      if (!original) return false;
      const origDob = parseDateToISO(original.dob);
      return (
        original.beneficiaryName !== b.name ||
        original.realtionship !== b.relationship ||
        origDob !== b.dob ||
        Number(original.rate) !== Number(b.rate) ||
        original.rateType !== b.rateType
      );
    });

    return hasNew || hasExistingChanges;
  }, [beneficiaries, originalBeneficiaries]);

  const hasInvalidRequiredFields = useMemo(() => {
    if (!beneficiaries || beneficiaries.length === 0) return true;

    const isComplete = (b) => {
      // Employee rows are always considered complete (auto-filled)
      if (b.relationship === "Employee") return true;
      
      return (
        Boolean(b.name && b.name.trim() !== "") &&
        Boolean(b.dob && b.dob !== "") &&
        (String(b.rate ?? '').trim() !== "")
      );
    };

    // For new rows, require completeness if any of their fields are touched
    const invalidNew = beneficiaries.some(b => {
      if (b.id) return false; // Skip existing rows
      if (b.relationship === "Employee") return false; // Employee rows are always valid
      
      const anyTouched =
        (b.name && b.name.trim() !== "") ||
        (b.dob && b.dob !== "") ||
        (String(b.rate ?? '').trim() !== "");
      return anyTouched && !isComplete(b);
    });

    // For existing rows, require completeness only if changed
    const invalidChanged = beneficiaries.some(b => {
      if (!b.id) return false;
      if (b.relationship === "Employee") return false; // Employee rows can't be changed
      
      const original = originalBeneficiaries.find(ob => ob._id === b.id);
      if (!original) return false;
      const origDob = parseDateToISO(original.dob);
      const changed = (
        original.beneficiaryName !== b.name ||
        original.realtionship !== b.relationship ||
        origDob !== b.dob ||
        Number(original.rate) !== Number(b.rate) ||
        original.rateType !== b.rateType
      );
      return changed && !isComplete(b);
    });

    return invalidNew || invalidChanged;
  }, [beneficiaries, originalBeneficiaries]);

  const handleAddRow = () => {
    const nextRelationship = findAvailableRelationship();
    if (!nextRelationship) {
      return;
    }
    const { rate, rateType } = getRateDetails(nextRelationship);
    setBeneficiaries((b) => [
      ...b,
      {
        id: undefined,
        name: "",
        relationship: nextRelationship,
        dob: "",
        rate,
        rateType,
        creditType: creditTypeId,
      },
    ]);
  };

  const handleFieldChange = (idx, field, value) =>
    setBeneficiaries((b) =>
      b.map((item, i) => {
        if (i !== idx) return item;

        if (field === "relationship") {
          const { rate, rateType } = getRateDetails(value);
          return {
            ...item,
            relationship: value,
            rate,
            rateType,
          };
        }

        return { ...item, [field]: value };
      })
    );

  const handleDeleteRow = async (idx, row) => {
    try {
      if (!row?.id) {
        setBeneficiaries(prev => prev.filter((_, i) => i !== idx));
        return;
      }
      if (typeof handleDeleteBeneficiary === "function") {
        await handleDeleteBeneficiary(row.id, creditId);
        // Refresh data from API after deletion
        if (creditId && typeof getAllBenificiaries === "function") {
          await getAllBenificiaries(creditId);
        }
        // Refresh credits list in AdditionalInfo
        if (typeof getAllCredits === "function") {
          await getAllCredits();
        }
      } else {
        setBeneficiaries(prev => prev.filter((_, i) => i !== idx));
      }
    } catch (e) {
      // silent; parent may show alert
    }
  };

  const handleSave = async () => {
    try {
      const newBeneficiaries = beneficiaries.filter(b => !b.id);
      const existingBeneficiaries = beneficiaries.filter(b => b.id);

      // Helper function to get beneficiary name and DOB based on relationship
      const getBeneficiaryData = (b) => {
        if (b.relationship === "Employee") {
          // For Employee, use data from userData (editProfile)
          const firstName = userData?.personalInfo?.firstName || "";
          const lastName = userData?.personalInfo?.surName || "";
          const beneficiaryName = `${firstName} ${lastName}`.trim() || "";
          const dob = parseDateToISO(userData?.personalInfo?.dob);
          return { beneficiaryName, dob };
        } else {
          // For other relationships, use form values
          return {
            beneficiaryName: b.name || "",
            dob: b.dob ? parseDateToISO(b.dob) : "",
          };
        }
      };

      const changedBeneficiaries = existingBeneficiaries
        .map(b => {
          const original = originalBeneficiaries.find(ob => ob._id === b.id);
          if (!original) return null;

          const { beneficiaryName, dob } = getBeneficiaryData(b);
          const origDob = parseDateToISO(original.dob);

          const hasChanged =
            original.beneficiaryName !== beneficiaryName ||
            original.realtionship !== b.relationship ||
            origDob !== dob ||
            Number(original.rate) !== Number(b.rate) ||
            original.rateType !== b.rateType;

          if (hasChanged) {
            return {
              beneficiary_id: b.id,
              creditType: creditId,
              beneficiaryName,
              realtionship: b.relationship,
              dob,
              rate: Number(b.rate),
              rateType: b.rateType,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (newBeneficiaries.length > 0) {
        const postPayload = {
          employee: userData?._id,
          credit_beneficiary: newBeneficiaries.map(b => {
            const { beneficiaryName, dob } = getBeneficiaryData(b);
            return {
              creditType: creditId,
              beneficiaryName,
              realtionship: b.relationship,
              dob,
              rate: Number(b.rate),
              rateType: b.rateType,
            };
          }),
        };
        await handleBeniSaved(postPayload);
        // Refresh data from API after save
        if (creditId && typeof getAllBenificiaries === "function") {
          await getAllCredits();
        }
      }

      if (changedBeneficiaries.length > 0) {
        const updatePayload = {
          employee: userData?._id,
          credit_beneficiary: changedBeneficiaries,
        };
        await handleUpdateBeneficiary(updatePayload);
        // Refresh data from API after update
        if (creditId && typeof getAllBenificiaries === "function") {
          await getAllCredits();
        }
      }

      onClose();
    } catch (error) {
      console.error("Error while saving beneficiaries:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.overlay}>
        <Paper sx={styles.modal} elevation={0}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>Manage Beneficiary</Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose} size="small">
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Box>
          <Typography sx={styles.desc}>
            Add and manage beneficiaries for this credit.
          </Typography>
          <Box sx={styles.tableCard}>
            <Table>
              <TableHead >
                <TableRow>
                  <TableCell sx={styles.thCell}>#</TableCell>
                  <TableCell sx={styles.thCell}>Beneficiary Name</TableCell>
                  <TableCell sx={styles.thCell}>Relationship</TableCell>
                  <TableCell sx={styles.thCell}>Date of Birth</TableCell>
                  <TableCell sx={styles.thCell}>Beneficiary Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {beneficiaries.map((b, idx) => {
                  const isEmployee = b.relationship === "Employee";
                  const employeeFirstName = userData?.personalInfo?.firstName || "";
                  const employeeLastName = userData?.personalInfo?.surName || "";
                  const employeeDOB = parseDateToISO(userData?.personalInfo?.dob);

                  return (
                    <TableRow key={idx} sx={isEmployee ? { backgroundColor: "#f5f5f5", opacity: 0.7 } : {}}>
                      <TableCell sx={styles.tdCell}>{idx + 1}</TableCell>
                      <TableCell sx={styles.tdCell}>
                        {isEmployee ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "14px", color: "#666", ...styles.input }}
                            value={`${employeeFirstName} ${employeeLastName}`}
                            disabled={isEmployee}
                          />
                        ) : (
                          <TextField
                            size="small"
                            placeholder="Name"
                            sx={styles.input}
                            value={b.name}
                            onChange={e =>
                              handleFieldChange(idx, "name", e.target.value)
                            }
                            disabled={isEmployee}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={styles.tdCell}>
                        <Select
                          size="small"
                          sx={styles.input}
                          value={b.relationship}
                          onChange={e =>
                            handleFieldChange(idx, "relationship", e.target.value)
                          }
                          disabled={isEmployee}
                        >
                          {availableRelationships.map(rel => {
                            const currentCount = relationshipCounts[rel] || 0;
                            const isOwnRelation = b.relationship === rel;
                            // For API-based relationships, don't enforce limits
                            const isAtLimit = availableRelationships === RELS ? (currentCount >= (RELATION_MAX[rel] || 999)) : false;
                            // Disable "Employee" option for all rows (can't select Employee for new rows)
                            const isEmployeeOption = rel === "Employee";
                            return (
                              <MenuItem
                                key={rel}
                                value={rel}
                                disabled={(!isOwnRelation && isAtLimit) || (isEmployeeOption && !isEmployee)}
                              >
                                {rel}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                      <TableCell sx={styles.tdCell}>
                        {isEmployee ? (
                          <TextField
                            type="date"
                            sx={styles.input}
                            value={employeeDOB || ""}
                            disabled={isEmployee}
                          />
                        ) : (
                          <TextField
                            type="date"
                            placeholder="mm/dd/yyyy"
                            sx={styles.input}
                            value={b.dob}
                            onChange={e =>
                              handleFieldChange(idx, "dob", e.target.value)
                            }
                            disabled={isEmployee}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={styles.tdCell}>
                        <TextField
                          type="number"
                          size="small"
                          sx={styles.input}
                          value={b.rate}
                          disabled={isEmployee}
                          placeholder={isEmployee ? "Auto-filled" : "Enter rate"}
                        />
                      </TableCell>
                      <TableCell align="center" sx={styles.tdCell}>
                        {beneficiaries.length > 1 && !isEmployee && (
                          <IconButton
                            aria-label="delete"
                            sx={styles.deleteBtn}
                            onClick={() => handleDeleteRow(idx, b)}
                            size="small"
                          >
                            <DeleteOutlineIcon color="error" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow >
                  <TableCell colSpan={4} align="right" sx={styles.totalLabel}>
                    Total
                  </TableCell>
                  <TableCell colSpan={2} sx={styles.totalValue}>
                    {totalRate}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          <Box sx={styles.row}>
            <Button
              sx={styles.addBtn}
              variant="outlined"
              startIcon={<span style={{ fontWeight: 400, fontSize: 17 }}>+</span>}
              onClick={handleAddRow}
              disabled={!canAddMoreBeneficiaries}
            >
              Add More Beneficiary
            </Button>
          </Box>
          <Box sx={styles.row}>
            <Button
              sx={styles.saveBtn}
              variant="contained"
              color="success"
              disabled={!hasChanges || hasInvalidRequiredFields}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default BeneficiaryModal;